import type { AnalyticsPoint } from './analytics'
import type { ProductForecastHorizonPointDto, ProductForecastUnavailableReason } from './product-analytics-storage'
import { formatRigaMonth, rigaMonthKey, type RigaMonthKey } from './month-identity'

export type MonthlyDynamicsMetric =
  | { availability: 'available'; value: number }
  | { availability: 'unavailable'; reason: ProductForecastUnavailableReason }

export interface MonthlyDynamicsForecastPoint {
  key: RigaMonthKey
  label: string
  state: 'current' | 'forecast'
  revenue: MonthlyDynamicsMetric
  profit: MonthlyDynamicsMetric
}

export interface MonthlyDynamicsCurrentPoint {
  key: RigaMonthKey
  label: string
  revenue: number
  profit: number
  orders: number
}

export interface MonthlyDynamicsModel {
  completed: AnalyticsPoint[]
  horizon: MonthlyDynamicsForecastPoint[]
  current: MonthlyDynamicsCurrentPoint | null
}

function metric(
  value: ProductForecastHorizonPointDto['turnover'] | ProductForecastHorizonPointDto['income'],
): MonthlyDynamicsMetric {
  return value.availability === 'available'
    ? { availability: 'available', value: value.estimateKopecks / 100 }
    : { availability: 'unavailable', reason: value.reason }
}

export function buildMonthlyDynamics(
  monthly: readonly AnalyticsPoint[],
  horizon: readonly ProductForecastHorizonPointDto[],
): MonthlyDynamicsModel {
  const normalizedHorizon = [...horizon]
    .map((point) => ({ ...point, month: rigaMonthKey(point.month) }))
    .sort((left, right) => left.month.localeCompare(right.month))
    .filter((point, index, all) => all.findIndex((candidate) => candidate.month === point.month) === index)
  const currentMonth = normalizedHorizon.find((point) => point.state === 'current')?.month
    ?? normalizedHorizon[0]?.month
  const completed = currentMonth
    ? monthly.filter((point) => rigaMonthKey(point.key) < currentMonth).slice(-12)
    : monthly.slice(-12)
  const currentFact = currentMonth ? monthly.find((point) => point.key === currentMonth) : undefined
  return {
    completed,
    horizon: normalizedHorizon.map((point) => ({
      key: point.month,
      label: formatRigaMonth(point.month),
      state: point.state,
      revenue: metric(point.turnover),
      profit: metric(point.income),
    })),
    current: currentFact && currentMonth
      ? {
          key: currentMonth,
          label: formatRigaMonth(currentMonth),
          revenue: currentFact.revenue,
          profit: currentFact.profit,
          orders: currentFact.orders,
        }
      : null,
  }
}
